/**
 * Cliente de la API de Asignaciones de packs/membresías y comprobantes (US-2.4, TG-53).
 *
 * Endpoints del contrato (`openapi.yaml`, namespace `/api/v1/admin/customers/{id}`):
 * - GET/POST      /packs                                   (listado / asignar)
 * - GET/DELETE    /packs/{assignmentId}                    (detalle / cancelar)
 * - GET/PUT/DELETE /membership                             (activa+historial / asignar-reemplazar / cancelar)
 * - GET/POST      /packs/{assignmentId}/receipts           (metadatos / subir multipart)
 * - GET           /packs/{assignmentId}/receipts/{id}      (metadatos + `downloadUrl`)
 * - GET           /membership/{assignmentId}/receipts[...] (mismo trío para membresías)
 *
 * Las escrituras exigen `SUPER_ADMIN` (RBAC del backend) y llevan `Idempotency-Key`
 * en `POST /packs` y `PUT /membership`. La subida usa `multipart/form-data` con el
 * campo `file`; `apiRequest` ya detecta `FormData` y no fija `Content-Type`.
 */
import type {
  AssignMembershipRequest,
  AssignPackRequest,
  CustomerReceipt,
  ListPackAssignmentsQuery,
  MembershipAssignment,
  MembershipAssignmentsResponse,
  PackAssignment,
  PackAssignmentsPage,
  ReceiptAssignmentType,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';
import { createIdempotencyKey } from '../../api/customerApi';

/** Campo multipart del contrato para subir un comprobante. */
export const CUSTOMER_RECEIPT_FIELD = 'file';

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: ListPackAssignmentsQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** Base del recurso del cliente, con el id codificado. */
function customerPath(customerId: string): string {
  return `/api/v1/admin/customers/${encodeURIComponent(customerId)}`;
}

/** Ruta de comprobantes según el tipo de asignación (dos rutas del contrato). */
function receiptsPath(
  customerId: string,
  type: ReceiptAssignmentType,
  assignmentId: string,
): string {
  const segment = type === 'pack' ? 'packs' : 'membership';
  return `${customerPath(customerId)}/${segment}/${encodeURIComponent(assignmentId)}/receipts`;
}

export const asignacionesApi = {
  /* ------------------------------------------------------------- Packs */

  listPackAssignments(
    customerId: string,
    params: ListPackAssignmentsQuery = {},
  ): Promise<PackAssignmentsPage> {
    return apiRequest<PackAssignmentsPage>(
      `${customerPath(customerId)}/packs${toQuery(params)}`,
      { retryOnUnauthorized: true },
    );
  },

  getPackAssignment(customerId: string, assignmentId: string): Promise<PackAssignment> {
    return apiRequest<PackAssignment>(
      `${customerPath(customerId)}/packs/${encodeURIComponent(assignmentId)}`,
    );
  },

  /**
   * Asigna un pack. La `Idempotency-Key` se resuelve UNA vez por intención: el
   * llamador la genera al abrir el modal y la reutiliza en reintentos.
   */
  assignPack(
    customerId: string,
    body: AssignPackRequest,
    idempotencyKey: string = createIdempotencyKey(),
  ): Promise<PackAssignment> {
    return apiRequest<PackAssignment>(`${customerPath(customerId)}/packs`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  /** Cancelación lógica idempotente (`204`). */
  cancelPackAssignment(customerId: string, assignmentId: string): Promise<void> {
    return apiRequest<void>(
      `${customerPath(customerId)}/packs/${encodeURIComponent(assignmentId)}`,
      { method: 'DELETE' },
    );
  },

  /* -------------------------------------------------------- Membresía */

  getMembership(customerId: string): Promise<MembershipAssignmentsResponse> {
    return apiRequest<MembershipAssignmentsResponse>(
      `${customerPath(customerId)}/membership`,
      { retryOnUnauthorized: true },
    );
  },

  /**
   * Asigna o reemplaza la membresía activa (`PUT` idempotente). Si había una activa,
   * el backend la cancela en la misma transacción y responde `200`; si no, `201`.
   */
  assignMembership(
    customerId: string,
    body: AssignMembershipRequest,
    idempotencyKey: string = createIdempotencyKey(),
  ): Promise<MembershipAssignment> {
    return apiRequest<MembershipAssignment>(`${customerPath(customerId)}/membership`, {
      method: 'PUT',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  /** Cancela la membresía activa (`204`, idempotente). */
  cancelMembership(customerId: string): Promise<void> {
    return apiRequest<void>(`${customerPath(customerId)}/membership`, {
      method: 'DELETE',
    });
  },

  /* ------------------------------------------------------ Comprobantes */

  listReceipts(
    customerId: string,
    type: ReceiptAssignmentType,
    assignmentId: string,
  ): Promise<CustomerReceipt[]> {
    return apiRequest<CustomerReceipt[]>(
      receiptsPath(customerId, type, assignmentId),
      { retryOnUnauthorized: true },
    );
  },

  /** Sube un comprobante PDF/JPG/PNG (multipart, campo `file`). */
  uploadReceipt(
    customerId: string,
    type: ReceiptAssignmentType,
    assignmentId: string,
    file: File,
  ): Promise<CustomerReceipt> {
    const form = new FormData();
    form.append(CUSTOMER_RECEIPT_FIELD, file, file.name);
    return apiRequest<CustomerReceipt>(
      receiptsPath(customerId, type, assignmentId),
      { method: 'POST', body: form },
    );
  },

  /** Metadatos del comprobante + `downloadUrl` firmada (el listado NO la incluye). */
  getReceipt(
    customerId: string,
    type: ReceiptAssignmentType,
    assignmentId: string,
    receiptId: string,
  ): Promise<CustomerReceipt> {
    return apiRequest<CustomerReceipt>(
      `${receiptsPath(customerId, type, assignmentId)}/${encodeURIComponent(receiptId)}`,
    );
  },
};
